import React, { FC, MouseEvent } from 'react';
import { APPLIED_FILTER_CHIP_MAX_WIDTH } from '@constants/appliedFilterChips';
import { Chip, Typography } from '@mui/material';
import { CloseCircleIcon } from '@utils/iconsDefs';

export type AppliedFilterChipProps = {
  label: string;
  summary: string;
  isActive?: boolean;
  disabled?: boolean;
  onClick: (event: MouseEvent<HTMLDivElement>) => void;
  onClear: () => void;
};

const noop = () => undefined;

/**
 * One applied filter category as a toolbar chip: the bold category label, a
 * colon, the selection summary and a ⓧ control that clears the whole
 * category.
 *
 * Purely presentational, so the model toolbar and the dashboard can both
 * render it from their own stores. The label never truncates; only the
 * summary ellipsises when the chip reaches its 240px cap, and there is
 * deliberately no tooltip — the full selection is read by opening the
 * category panel, which is what the click does. The ⓧ is passed explicitly
 * rather than left to the theme default so it carries an accessible name and
 * renders the same glyph when mounted without the theme.
 *
 * `isActive` marks the chip whose category panel is open. It and hover share
 * one appearance — the resting fill with a 1px ring — drawn as an inset
 * shadow rather than a border so the chip's width never changes with its
 * state: the row measures these widths to decide how many fit, and a border
 * would make a hovered chip wider than the copy that was measured.
 *
 * `disabled` renders the chip read-only: dimmed, unfocusable, its click and
 * its ⓧ inert. Both handlers are swapped for no-ops rather than removed: MUI
 * only forwards `disabled` to the chip's root while the chip has a click
 * handler, and the ⓧ stays in the DOM so a disabled chip measures exactly as
 * its enabled twin.
 */
const AppliedFilterChip: FC<AppliedFilterChipProps> = ({
  label,
  summary,
  isActive = false,
  disabled = false,
  onClick,
  onClear,
}) => (
  <Chip
    variant="filter"
    className={isActive ? 'selected' : undefined}
    disabled={disabled}
    onClick={disabled ? noop : onClick}
    onDelete={disabled ? noop : onClear}
    deleteIcon={<CloseCircleIcon aria-label={`Clear ${label}`} />}
    sx={{ maxWidth: APPLIED_FILTER_CHIP_MAX_WIDTH }}
    label={
      <>
        <Typography
          component="span"
          variant="h5"
          color="grey.1000"
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          {label}:
        </Typography>

        <Typography
          component="span"
          variant="body1"
          color="grey.900"
          noWrap
          sx={{ minWidth: 0 }}
        >
          {summary}
        </Typography>
      </>
    }
  />
);

export default AppliedFilterChip;
